import { useState, useRef, useEffect } from 'react';
import api from '@/utils/ky';
import { EndSessionRequest, SessionSummaryResponse } from '@/types/session-summary';
import { SessionSummaryData } from '@/components/ui/session-summary';

/**
 * Hook for managing session ending and summary data
 */
export function useSessionSummary() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryData, setSummaryData] = useState<SessionSummaryData | null>(null);

  // References for polling
  const sessionInfoRef = useRef<{sessionId?: string; deviceId?: string} | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);
  const MAX_RETRY_COUNT = 20; // Increased to 20 retries (up to 3+ minutes total)
  
  // Adaptive retry intervals: start slower, then get faster
  const getRetryInterval = (attemptNumber: number): number => {
    if (attemptNumber <= 3) return 10000; // First 3 attempts: 10 seconds
    if (attemptNumber <= 8) return 8000;  // Next 5 attempts: 8 seconds  
    if (attemptNumber <= 15) return 5000; // Next 7 attempts: 5 seconds
    return 3000; // Final 5 attempts: 3 seconds
  };

  // Cleanup polling on component unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Polling function to get an updated summary
   */
  const pollForCompletedSummary = async () => {
    // Clear any existing timeout
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    // Only continue if we have session info
    if (!sessionInfoRef.current?.sessionId) {
      console.warn('[useSessionSummary] No session ID to poll for');
      return;
    }

    try {
      const currentAttempt = retryCountRef.current + 1;
      const nextInterval = getRetryInterval(currentAttempt);
      console.log(`[useSessionSummary] Polling for completed summary (attempt ${currentAttempt}/${MAX_RETRY_COUNT}, next in ${nextInterval}ms)`);

      // Prepare headers with device ID if available
      const headers: Record<string, string> = {};
      if (sessionInfoRef.current.deviceId) {
        headers['x-device-id'] = sessionInfoRef.current.deviceId;
      }

      // Poll the backend for an updated summary
      const response = await api
        .get(`api/app/sessions/${sessionInfoRef.current.sessionId}/summary`, { headers })
        .json<SessionSummaryResponse>();

      console.log('[useSessionSummary] Polling response:', response);

      // If we get a success status with a full summary, update the data
      if (response.status === 'success' && response.summary) {
        console.log('[useSessionSummary] Received completed summary');

        // Transform API response to component data format
        const summaryData: SessionSummaryData = {
          title: response.summary.moduleTitle,
          keyChallenge: response.summary.keyChallenge,
          discussionInsights: response.summary.discussionInsights,
          behaviouralAnalysis: response.summary.behaviouralAnalysis,
          completionStatus: response.summary.completionStatus,
        };

        setSummaryData(summaryData);

        // Reset polling state since we're done
        sessionInfoRef.current = null;
        retryCountRef.current = 0;
        return;
      }

      // If we get 'generating' status, increment retry count and try again if under max
      if (response.status === 'generating') {
        retryCountRef.current++;

        // If we haven't hit the max retries, schedule another poll with adaptive interval
        if (retryCountRef.current < MAX_RETRY_COUNT) {
          const nextInterval = getRetryInterval(retryCountRef.current);
          console.log(`[useSessionSummary] Still generating, retrying in ${nextInterval}ms (attempt ${retryCountRef.current}/${MAX_RETRY_COUNT})`);
          pollingTimeoutRef.current = setTimeout(pollForCompletedSummary, nextInterval);
        } else {
          console.log('[useSessionSummary] Reached maximum retry count, giving up');
          // Reset retry count for future attempts
          retryCountRef.current = 0;
        }
      }
    } catch (err) {
      console.error('[useSessionSummary] Error polling for summary:', err);

      // Increment retry count and try again if under max
      retryCountRef.current++;
      if (retryCountRef.current < MAX_RETRY_COUNT) {
        const nextInterval = getRetryInterval(retryCountRef.current);
        console.log(`[useSessionSummary] Error occurred, retrying in ${nextInterval}ms (attempt ${retryCountRef.current}/${MAX_RETRY_COUNT})`);
        pollingTimeoutRef.current = setTimeout(pollForCompletedSummary, nextInterval);
      } else {
        console.log('[useSessionSummary] Reached maximum retry count after errors, giving up');
        // Reset retry count for future attempts
        retryCountRef.current = 0;
      }
    }
  };

  /**
   * End the current session and request a summary
   */
  const endSession = async (request: EndSessionRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('[useSessionSummary] Ending session and requesting summary:', request);

      // Ensure the device ID is set in the request
      if (!request.deviceId) {
        console.warn('[useSessionSummary] No device ID provided for session. It may not be properly tracked.');
      }

      // Make API request to end session and get summary
      // Ensure we explicitly add the device ID as a header
      const headers: Record<string, string> = {};
      if (request.deviceId) {
        // Log the device ID being sent to the endpoint
        console.log('[useSessionSummary] Adding device ID to end session request:', request.deviceId);
        headers['x-device-id'] = request.deviceId;
      } else {
        console.warn('[useSessionSummary] No device ID available for end session request');
      }

      const response = await api
        .post('api/app/sessions/end', {
          json: request,
          headers
        })
        .json<SessionSummaryResponse>();

      console.log('[useSessionSummary] Received session summary response:', response);
      
      // Handle different response statuses
      if (response.status === 'error') {
        throw new Error(response.errorMessage || 'Failed to generate session summary');
      }
      
      if (response.status === 'generating') {
        console.log('[useSessionSummary] Received generating status with data:', response.summary);

        // Extract data from the response to show meaningful information
        const summaryData: SessionSummaryData = {
          title: response.summary?.moduleTitle || 'Your Coaching Session',
          completionStatus: response.summary?.completionStatus || 'completed',
          // Add any other fields available in the response
          keyChallenge: "Thank you for your session. The coach is analyzing your conversation.",
          discussionInsights: ["Your session has been recorded and is being evaluated."],
        };

        setSummaryData(summaryData);

        // Save session info for polling
        if (response.summary?.sessionId) {
          console.log('[useSessionSummary] Starting poll for completed summary, session ID:', response.summary.sessionId);
          sessionInfoRef.current = {
            sessionId: response.summary.sessionId,
            deviceId: request.deviceId
          };

          // Reset retry counter
          retryCountRef.current = 0;

          // Start polling with the initial adaptive interval
          const initialInterval = getRetryInterval(1);
          console.log(`[useSessionSummary] Starting polling with initial interval: ${initialInterval}ms`);
          pollingTimeoutRef.current = setTimeout(pollForCompletedSummary, initialInterval);
        }
      }
      
      if (response.status === 'success' && response.summary) {
        // Transform API response to component data format
        const summaryData: SessionSummaryData = {
          title: response.summary.moduleTitle,
          keyChallenge: response.summary.keyChallenge,
          discussionInsights: response.summary.discussionInsights,
          behaviouralAnalysis: response.summary.behaviouralAnalysis,
          completionStatus: response.summary.completionStatus,
        };
        
        setSummaryData(summaryData);
      }
      
      return true;
    } catch (err) {
      console.error('[useSessionSummary] Error ending session:', err);
      
      const errorMessage = 
        err instanceof Error ? err.message : 'Failed to end session and get summary';
      
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Clear the current summary data and cancel any pending polling
   */
  const clearSummary = () => {
    setSummaryData(null);
    setError(null);

    // Clear any active polling
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    // Reset session info and retry count
    sessionInfoRef.current = null;
    retryCountRef.current = 0;
  };
  
  return {
    endSession,
    clearSummary,
    isLoading,
    error,
    summaryData,
  };
}